import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { SafeUrl } from '@angular/platform-browser';

import {
  AiSearchAdjustmentType,
  Candidate,
  SearchAdjustmentProfileState,
} from '../../../../../shared/models';
import {
  CandidateCardHandlerService,
  CandidateImageHandlerService,
} from '../../../../../shared/services';
import { CandidateProfileHandlerService } from '../../../services/candidates/candidate-profile-handler.service';

@Component({
  selector: 'app-candidate-card',
  templateUrl: './candidate-card.component.html',
  styleUrls: ['./candidate-card.component.scss'],
  providers: [CandidateCardHandlerService, CandidateProfileHandlerService],
  standalone: false,
})
export class CandidateCardComponent implements OnInit, AfterViewInit {
  constructor(
    private imageHandler: CandidateImageHandlerService,
    private handlerService: CandidateCardHandlerService,
  ) {}

  @Input() type: 'standard' | 'forFolder' = 'standard';
  @Input() candidate: Candidate;
  @Input() folderId = '';
  @Input() index = 0;
  @Input() adjustmentState: SearchAdjustmentProfileState | null = null;

  // forFolder type only:
  @Output() removeFromFolder = new EventEmitter<null>();
  @Output() changeFolderStatus = new EventEmitter<null>();

  @Output() selectCandidate = new EventEmitter<boolean>();
  @Output() adjustmentChange = new EventEmitter<AiSearchAdjustmentType | null>();

  private updatePicture = new BehaviorSubject<SafeUrl>(null);
  private unsubscribe$ = new Subject<void>();

  revealed = false;
  protected adjustmentInProgressType: AiSearchAdjustmentType | null = null;

  picture$: Observable<SafeUrl> = this.updatePicture.asObservable();

  ngOnInit(): void {
    this.handlerService.setCandidateInfo(this.candidate);
  }

  ngAfterViewInit(): void {
    if (this.candidate?.pictureUrl) {
      this.updatePicture.next(this.candidate.pictureUrl);

      return;
    }

    this.imageHandler
      .getCandidateImageUrl(this.candidate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((pictureUrl: SafeUrl) => {
        this.candidate.pictureUrl = pictureUrl as string;

        this.updatePicture.next(pictureUrl);
      });
  }

  toggleRevealCard(): void {
    this.revealed = !this.revealed;

    this.handlerService.updateRevealedCardInfo(this.revealed);
  }

  onPictureError(): void {
    this.candidate.pictureUrl = '';

    this.updatePicture.next('');
  }

  // *************** forFolder type only: ***************

  onChangeFolderStatus(): void {
    if (this.type === 'forFolder') {
      this.changeFolderStatus.emit();
    }
  }

  onSelectCandidate(): void {
    this.candidate.isSelected = !this.candidate.isSelected;

    this.selectCandidate.emit(this.candidate.isSelected);
  }

  onRemoveFromFolder(): void {
    if (this.type == 'forFolder') {
      this.removeFromFolder.emit(null);
    }
  }

  onAdjustmentClick(type: AiSearchAdjustmentType): void {
    if (
      !this.adjustmentState?.canAdd ||
      this.adjustmentState?.isInProgress ||
      this.adjustmentState?.isForbidden
    ) {
      return;
    }

    this.adjustmentInProgressType = type;
    const newType = this.adjustmentState.currentType === type ? null : type;
    this.adjustmentChange.emit(newType);
  }
}
